import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { pushThirdPartySummaries } from '@/api/cms/thirdPartySummaries';
import type { PlayerJackpotWin } from '@/api/common';
import { Modal } from '@/components/Modal';
import { JackpotWinnerModal } from '@/components/modals/JackpotWinnerModal';
import { findAvailableModal } from '@/helpers/thirdPartySummaries';
import { useGetCommonStore } from '@/store';
import { timestampByUTC } from '../helpers/dateTime';

const formatModalId = (createdAt: string) =>
  `JACKPOT_WIN_${timestampByUTC(createdAt)}`;

export const useJackpotWinnerModal = () => {
  const useCommonStore = useGetCommonStore();

  const { user, playerJackpotWins, thirdPartySummaries } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      playerJackpotWins: state.playerJackpotWins,
      thirdPartySummaries: state.thirdPartySummaries,
    }))
  );

  const [isOpenJackpot, setOpenJackpot] = useState<PlayerJackpotWin>();

  const availableModal = useMemo(() => {
    if (!(user?.id && playerJackpotWins?.length)) return;

    const ids = playerJackpotWins.map(({ createdAt }) =>
      formatModalId(createdAt)
    );
    const modalId = thirdPartySummaries
      ? findAvailableModal(ids)(thirdPartySummaries)
      : undefined;

    return {
      modalId: modalId || undefined,
      modalData: modalId ? playerJackpotWins[ids.indexOf(modalId)] : undefined,
    };
  }, [playerJackpotWins, thirdPartySummaries, user]);

  useEffect(() => {
    if (!(user?.id && availableModal?.modalData && availableModal?.modalId))
      return;

    setOpenJackpot(availableModal.modalData);
    pushThirdPartySummaries({
      userId: user?.id,
      modalId: availableModal.modalId,
    });
  }, [availableModal, user?.id]);

  return isOpenJackpot ? (
    <Modal>
      <JackpotWinnerModal
        jackpotWin={isOpenJackpot}
        onClose={() => setOpenJackpot(undefined)}
      />
    </Modal>
  ) : null;
};
